import { useCallback, useEffect, useState } from "@wordpress/element";

import ContactForm from "./ContactForm.jsx";
import SlidingPane from "react-sliding-pane";
import { __ , _x} from "@wordpress/i18n";
import { useCookie } from "../../../../manitoba-stores/react/services/cookie.js";
import { useSubmitLead } from "../services/submit.js";

export default function QuickAccess({ title, acceptTerms, needs = [] }) {
  const [isOpened, setOpened] = useState(false);
  const [activeStoreId] = useCookie("active-store", "");
  const [storeId, setStoreId] = useState(activeStoreId);
  const [successMessage, setSuccessMessage] = useState();
  const [kind, setKind] = useState("contact"); // callback | contact

  const open = useCallback(
    ({ kind, storeId = null, successMessage = null }) => {
      setKind(kind);
      // `activeStoreId` est figé à sa valeur du premier rendu dans cette
      // closure, aux dépendances volontairement vides : la faire dépendre du
      // cookie rattacherait les écouteurs de clic, que l'effet plus bas
      // n'enlève jamais. On ne remplace donc la valeur courante que si le
      // déclencheur en fournit une — l'effet qui suit le cookie la tient
      // déjà à jour.
      setStoreId((current) => storeId || current);
      setSuccessMessage(successMessage);
      setOpened(true);
    },
    []
  );
  const close = useCallback(() => setOpened(false), []);

  const [{ loading, error, data }, submitLead] = useSubmitLead();

  const submitForm = useCallback(function (values, { setSubmitting }) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'gtm.formSubmit',
      'form_type': 'leads_quickaccess',
      'agency_id': values.store ? values.store.id : '',
      'agency_name': values.store ? values.store.name : '',
      'gtm.element': document.querySelector('.leads-form'),
      'gtm.elementClasses': 'leads-form'
    });
    
    // Le champ « Votre demande concerne » porte désormais l'identifiant XEFI
    // pour un prospect. Or neuf templates d'email affichent `request` en clair :
    // on retrouve donc le libellé pour le leur laisser, et l'identifiant voyage
    // à part, vers le seul appel à l'API.
    const need = needs.find((n) => String(n.id) === String(values.request));

    submitLead({
      ...values,
      // L'agence venue du cookie n'a qu'un identifiant : son nom est absent, et
      // JSON.stringify omettrait la clé, laissant le serveur lire un index
      // inexistant.
      store: values.store && values.store.name ? values.store.name : "",
      // L'agence du cookie « active-store » est déjà la valeur initiale du champ,
      // y compris pour un prospect qui ne le voit pas : son identifiant voyage
      // à part, pour le seul appel à l'API.
      storeId: values.store ? values.store.id : "",
      needId: values.profile === "prospect" ? values.request : "",
      request: need ? need.label : values.request,
    });
    setSubmitting(false);
  }, [needs]);

  useEffect(
    function () {
      if (loading) return;
      if (data) {
        setTimeout(close, 5000);
      }
    },
    [loading, error, data]
  );

  useEffect(
    function () {
      setStoreId(activeStoreId);
    },
    [activeStoreId]
  );

  useEffect(
    function () {
      document
        .querySelectorAll(
          '[data-role="manitoba-leads-quickaccess-trigger-contact"]'
        )
        .forEach(function (el) {
          el.addEventListener("click", () =>
            open({
              kind: "contact",
              storeId: el.getAttribute("data-store"),
              successMessage: el.getAttribute("data-success"),
            })
          );
        });
      document
        .querySelectorAll(
          '[data-role="manitoba-leads-quickaccess-trigger-callback"]'
        )
        .forEach(function (el) {
          el.addEventListener("click", () =>
            open({
              kind: "callback",
              storeId: el.getAttribute("data-store"),
              successMessage: el.getAttribute("data-success"),
            })
          );
        });
      document
        .querySelectorAll('[href="#contact-store"]')
        .forEach(function (el) {
          el.addEventListener("click", () =>
            open({
              kind: "contact",
              storeId: el.getAttribute("data-store"),
              successMessage: el.getAttribute("data-success"),
            })
          );
        });
      document
        .querySelectorAll('[data-role="#callback-store"]')
        .forEach(function (el) {
          el.addEventListener("click", () =>
            open({
              kind: "callback",
              storeId: el.getAttribute("data-store"),
              successMessage: el.getAttribute("data-success"),
            })
          );
        });
    },
    [open]
  );

  return (
    <SlidingPane
      closeIcon={
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M19.3611 2.74348L12.0366 10.068L19.2927 17.3241L17.2847 19.3322L10.0285 12.0761L2.74408 19.3605L0.639335 17.2557L7.92378 9.9713L0.667647 2.71517L2.67572 0.7071L9.93185 7.96323L17.2563 0.638737L19.3611 2.74348Z"
            fill="white"
            stroke="black"
            stroke-width="0.5"
          />
        </svg>
      }
      className="leads-quickaccess-panel"
      overlayClassName="leads-quickaccess-overlay"
      isOpen={isOpened}
      onRequestClose={close}
      title={
        kind === "contact"
          ? _x("Contacter votre agence", "Frontend panel", "manitoba-leads")
          : kind === "callback"
          ? _x("Être appelé rapidement", "Frontend panel", "manitoba-leads")
          : title
      }
      width="470px"
    >
      {!!loading && (
        <p class="loading">
          <span class="loading-message">
            {_x("Chargement...", "Frontend forms", "manitoba-leads")}
          </span>
        </p>
      )}

      {!!data ? (
        <p class="success">
          <span
            class="success-message"
            dangerouslySetInnerHTML={{
              __html: (
                successMessage ||
                _x(
                  "Votre message a été envoyé !",
                  "Frontend forms",
                  "manitoba-leads"
                )
              ).replaceAll(/\n/gim, "<br />"),
            }}
          ></span>
        </p>
      ) : (
        <ContactForm
          onSubmit={submitForm}
          storeId={storeId}
          kind={kind}
          acceptTerms={acceptTerms}
          needs={needs}
          //Retour client pour supprimer le message par défaut
          //  message={sprintf(
          //   __(
          //     `Bonjour,\n\nJe cherche un conseil à propos de %s.`,
          //     "Frontend forms",
          //     "manitoba-leads"
          //   ),
          //   title || document.title
          // )}
        />
      )}

      {!!error && (
        <p class="error">
          {/* `error` est l'objet d'erreur d'Axios : le rendre tel quel fait
              planter le composant (React #31) et le message devient
              invisible, panneau figé sur « Chargement... ». */}
          <span class="error-message">{error.message || String(error)}</span>
        </p>
      )}
    </SlidingPane>
  );
}
